import React, { useContext, useEffect, useState } from "react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { Button, Col, Form, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { BsArrowDown } from "react-icons/bs";
import { AuthContext, funcionalidades } from "../../../context/AuthContext";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import DateTimeInputFormik from "../../../components/UI/DateTimeInput/DateTimeInputFormik";
import SelectInputCachedFormik from "../../../components/UI/SelectInput/SelectInputCachedFormik";
import SelectFaseFormik from "../../../components/UI/SelectInput/SelectFaseFormik";
import SelectInput from "../../../components/UI/SelectInput/SelectInput";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";
import useColumns from "./hooks/useColumns";
import ExecutarOrdemServicoDetalhes from "../ExecutarOrdemServico/components/ExecutarOrdemServicoDetalhes";
import SelectDiretor from "../../../components/UI/SelectInput/SelectDiretor";
import CheckBoxProcedimentos from "./CheckBoxProcedimentos";
import useRelatorioXlsx from "./hooks/useRelatorioXlsx";
import { formatarData } from "../../../components/Formatters/DateTime";
import constants from "../../Constants";

const situacao = [
  { key: "0", value: "", text: "Selecione" },
  { key: "1", value: "A", text: "ABERTA" },
  { key: "2", value: "C", text: "CANCELADA" },
  { key: "3", value: "E", text: "ENCERRADA" },
  { key: "4", value: "E,T", text: "ENCERRADA + PAT" },
  // { key: "5", value: "P", text: "PAGA" },
  { key: "6", value: "T", text: "PAT" },
  { key: "7", value: "S", text: "PARA ASSINATURA" },
];

const validationSchema = Yup.object().shape({
  nuOs: Yup.string().default(""),
  sqSituacao: Yup.string().default(""),
  sqSetor: Yup.string().default(""),
  nuTipoAcaoFiscal: Yup.string().default(""),
  nuFase: Yup.string().default(""),
  dtInicio: Yup.string().default(""),
  dtFim: Yup.string().default(""),
  numAuto: Yup.string().default(""),
  matriculaAuditor: Yup.string().default(""),
  nomeAuditor: Yup.string().default(""),
  nomeDiretor: Yup.string().default(""),
  codUsuario: Yup.string().default(""),
  procedimentos: Yup.array().default([]),
  dtInicioEncerramento: Yup.string().default(""),
  dtFimEncerramento: Yup.string().default(""),
  identificador: Yup.string()
    .default("")
    .test("identificador", "Identificador inválido", (value) => {
      const identificador = value.replace(/\D/g, "");

      if (
        identificador.length !== 14 &&
        identificador.length !== 11 &&
        identificador.length !== 9 &&
        identificador.length !== 0
      )
        return false;

      return true;
    }),
});

const ConsultarOrdemServico = ({ location, history }) => {
  const [rows, setRows] = useState([]);
  const { user } = useContext(AuthContext);
  const { request: requestForm, data: dataForm, errors: errorForm, isLoading } = useApiClient();
  const columns = useColumns();
  const { isAllowed } = useContext(AuthContext);

  const initialValues = {};

  validationSchema._nodes.forEach((key) => {
    if (key === "procedimentos") {
      initialValues[key] = [];
    } else {
      initialValues[key] = "";
    }
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      setRows([]);
      const params = {
        nuOs: values.nuOs,
        sqSituacao: values.sqSituacao,
        sqSetor: values.sqSetor,
        nuTipoAcaoFiscal: values.nuTipoAcaoFiscal,
        nuFase: values.nuFase,
        dtInicio: values.dtInicio,
        dtFim: values.dtFim,
        numAuto: values.numAuto,
        matriculaAuditor: values.matriculaAuditor,
        nomeAuditor: values.nomeAuditor,
        nomeDiretor: values.nomeDiretor,
        codUsuario: user.codigo,
        permissao: isAllowed(funcionalidades.os.visaoGerencial) || isAllowed(funcionalidades.os.visaoAuditor) ? 1 : 2,
        procedimentos: values.procedimentos,
        dtInicioEncerramento: values.dtInicioEncerramento,
        dtFimEncerramento: values.dtFimEncerramento,
        identificador: values.identificador.replace(/\D/g, ""),
      };
      requestForm(apiRoutes.fiscalizacao.ordemservico.consultarOs, params);
    },
  });

  const { setSubmitting, setErrors, errors, isSubmitting, isValid } = formik;

  useEffect(() => {
    if ((dataForm || errorForm) && isSubmitting && !isLoading) setSubmitting(false);

    if (errorForm && isValid && !isLoading) setErrors({ ...errors, ...errorForm });

    if (dataForm?.result) {
      if (dataForm.result.length && !dataForm.result[0].totalRows) {
        return;
      }

      setRows(dataForm.result || []);
    }
  }, [setSubmitting, setErrors, errors, isSubmitting, isValid, dataForm, errorForm, isLoading]);

  useEffect(() => {
    const { state } = location;

    if (state !== undefined) {
      const { codigo, tipo, orgao } = state;

      const objeto = JSON.parse(localStorage.getItem("user"));
      const { cnpjCpf } = objeto;
      const params = {
        codigo: codigo || "0",
        tipo,
        orgao: orgao || "0",
        codUsuario: cnpjCpf,
        permissao: isAllowed(funcionalidades.os.visaoGerencial) ? 1 : 2,
      };
      requestForm(apiRoutes.fiscalizacao.ordemservico.consultarOSInformacaoSintetica, params);
    }
  }, [location, isAllowed, requestForm]);

  function resetForm() {
    formik.resetForm({
      values: {
        nuOs: "",
        sqSituacao: "",
        sqSetor: "",
        nuTipoAcaoFiscal: "",
        nuFase: "",
        dtInicio: "",
        dtFim: "",
        numAuto: "",
        matriculaAuditor: "",
        nomeAuditor: "",
        nomeDiretor: "",
        codUsuario: "",
        procedimentos: [],
        dtInicioEncerramento: "",
        dtFimEncerramento: "",
        identificador: "",
      },
    });
  }

  function isExecutor(row) {
    const executor = row.executores?.filter((exec) => exec.includes(user.nome)).length > 0;
    const rota = "consultarOs/consulta";

    if (isAllowed(funcionalidades.os.visaoGerencial) || isAllowed(funcionalidades.os.visaoAuditor)) {
      if (isAllowed(funcionalidades.os.visaoGerencial)) {
        history.push({
          pathname: rota,
          state: { itemKey: row.codigo },
        });
      } else if (isAllowed(funcionalidades.os.visaoAuditor)) {
        if (executor && row.dataCiencia === null && funcionalidades.os.visaoAuditor) {
          toast.warning("Atenção! Para prosseguir é necessário que seja registrada a sua ciência");
        } else if (!executor && isAllowed(funcionalidades.os.visaoAuditor)) {
          toast.warning("Usuário não é executor da OS");
        } else {
          history.push({
            pathname: rota,
            state: { itemKey: row.codigo },
          });
        }
      }
    } else {
      toast.warning("Usuário não tem permissão Gerencial ou de Auditor");
    }
  }

  const { generateXlsx } = useRelatorioXlsx();

  // Mapeamento de colunas: chave JSON -> nome da coluna na planilha
  const columnMap = {
    codigo: "Nº OS",
    descTipoAcaoFiscal: "Tipo de Ação",
    nuInscEstadual: "Inscrição Estadual",
    nuCnpj: "CNPJ",
    contribuinte: "Razão Social",
    status: "Status",
    dtInclusao: "Data Abertura",
    dataEncerramento: "Data Encerramento",
    listaProdutosAcaoFiscal: "Auto de Infração",
    cdPgeProcesso: "Processo",
    listaValorIcmsTotal: "Valor Total ICMS Lançado - AI (R$)",
    listaValorMultaTotal: "Valor Multa Lançado - AI (R$)",
    vlIcmsDevido: "Valor ICMS Devido",
    vlIcmsRecolhido: "Valor ICMS Recolhido",
    vlIcmsParcelado: "Valor ICMS Parcelado",
    vlIcmsEstornado: "Valor ICMS Estornado",
    listaExecutores: "Executores",
  };

  const handleExport = (data) => {
    const listaData = data.map((x) => ({
      ...x,
      dtInclusao: formatarData(x.dtInclusao, "dd/MM/yyyy HH:mm"),
      dataEncerramento: formatarData(x.dataEncerramento, "dd/MM/yyyy HH:mm"),
    }));

    generateXlsx(
      listaData,
      columnMap,
      "Relatorio.xlsx",
      "Relatório",
      "RELATÓRIO DE ORDENS DE SERVIÇO",
      [10, 20, 20, 25, 40, 20, 20, 20, 30, 30, 30, 30, 30, 30, 30, 30, 40],
    );
  };

  return (
    <>
      <h1 className="h3 mb-4 text-gray-800">Consultar OS</h1>
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="font-weight-bold text-primary flex-grow-1 ml-2 mt-2">Consulta de OS</h6>
              <div className="card-body">
                <FormAlert formik={formik} data={dataForm} errors={errorForm} isLoading={isLoading} />
                <Form noValidate onSubmit={formik.handleSubmit}>
                  <Row>
                    <Form.Group as={Col} sm={6} md={4}>
                      <Form.Label>Nº OS</Form.Label>
                      <TextInputFormik formik={formik} field="nuOs" />
                    </Form.Group>
                    <Form.Group as={Col} sm={6} md={4}>
                      <Form.Label>Nº do Auto</Form.Label>
                      <TextInputFormik formik={formik} field="numAuto" />
                    </Form.Group>
                    <Form.Group as={Col} sm={6} md={4}>
                      <Form.Label>IE / CNPJ / CPF</Form.Label>
                      <TextInputFormik
                        placeholder="Informe um IE ou um CNPJ ou um CPF"
                        formik={formik}
                        field="identificador"
                      />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={6} md={4}>
                      <Form.Label>Situação</Form.Label>
                      <SelectInput formik={formik} field="sqSituacao" options={situacao} />
                    </Form.Group>
                    <Form.Group as={Col} sm={6} md={4}>
                      <Form.Label>Setor</Form.Label>
                      <SelectInputCachedFormik formik={formik} field="sqSetor" tipoItem="setor-os" />
                    </Form.Group>
                    <Form.Group as={Col} sm={6} md={4}>
                      <Form.Label>Fase</Form.Label>
                      <SelectFaseFormik formik={formik} field="nuFase" />
                    </Form.Group>
                    <Form.Group as={Col} sm={6} md={4}>
                      <Form.Label>Matrícula Auditor</Form.Label>
                      <TextInputFormik formik={formik} field="matriculaAuditor" />
                    </Form.Group>
                    <Form.Group as={Col} sm={6} md={4}>
                      <Form.Label>Nome Auditor</Form.Label>
                      <TextInputFormik formik={formik} field="nomeAuditor" />
                    </Form.Group>
                    <Form.Group as={Col} sm={6} md={4}>
                      <SelectDiretor formik={formik} field="nomeDiretor" />
                    </Form.Group>
                    <Form.Group as={Col} sm={6} md={4}>
                      <Form.Label>Tipo de Ação Fiscal</Form.Label>
                      <SelectInputCachedFormik formik={formik} field="nuTipoAcaoFiscal" tipoItem="tipo-acao-fiscal" />
                    </Form.Group>
                    <Form.Group as={Col} sm={6} md={4}>
                      <Form.Label>Geração da OS</Form.Label>
                      <div className="row">
                        <div className="col-sm-4">
                          <DateTimeInputFormik formik={formik} field="dtInicio" />
                        </div>
                        a
                        <div className="col-sm-4">
                          <DateTimeInputFormik formik={formik} field="dtFim" />
                        </div>
                      </div>
                    </Form.Group>

                    <Form.Group as={Col} sm={6} md={4}>
                      <Form.Label>Encerramento da OS</Form.Label>
                      <div className="row">
                        <div className="col-sm-4">
                          <DateTimeInputFormik formik={formik} field="dtInicioEncerramento" />
                        </div>
                        a
                        <div className="col-sm-4">
                          <DateTimeInputFormik formik={formik} field="dtFimEncerramento" />
                        </div>
                      </div>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={12}>
                      <CheckBoxProcedimentos formik={formik} field="procedimentos" />
                    </Form.Group>
                  </Row>
                  <Row className="mt-2">
                    <Form.Group as={Row} style={{ marginLeft: 0 }} className="d-flex flex-row">
                      <Col sm="12">
                        <Button className="btn btn-primary btn-md mr-2" type="submit">
                          Buscar
                        </Button>
                        <Button
                          className="btn btn-primary btn-md mr-2"
                          variant="light"
                          onClick={() => {
                            resetForm();
                          }}
                        >
                          Limpar
                        </Button>
                      </Col>
                    </Form.Group>
                  </Row>
                </Form>
              </div>
            </div>

            <div className="col-lg-12">
              <div className="card-body white">
                <DataTable
                  noHeader
                  keyField="sqAndamento"
                  columns={columns}
                  data={rows}
                  progressPending={isLoading}
                  noDataComponent="Nenhum registro para exibir"
                  progressComponent={<TextSpinner />}
                  pointerOnHover
                  sortable
                  sortIcon={<BsArrowDown />}
                  onRowClicked={(row) => isExecutor(row)}
                  pagination
                  paginationComponentOptions={constants.paginationComponentOptions}
                  expandableRows={!!ExecutarOrdemServicoDetalhes}
                  expandableRowsComponent={ExecutarOrdemServicoDetalhes}
                />
                {rows.length > 0 && isAllowed(funcionalidades.os.planilhaGerar) && (
                  <Row>
                    <Form.Group as={Row} style={{ marginLeft: 0 }} className="d-flex flex-row">
                      <Col sm="12">
                        <Button onClick={() => handleExport(rows)} className="btn btn-primary btn-md mr-2">
                          Gerar xlsx
                        </Button>
                      </Col>
                    </Form.Group>
                  </Row>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConsultarOrdemServico;
